import { Inject, Injectable } from '@angular/core';
import { EntityService } from "@shared/services/entity.service";
import { EntityFeatureNameEnum } from "@shared/contracts/entity-store.contract";
import { PaymentProviderNameEnum, PaymentProviderType } from "@generated/graphql";
import { select, Store } from "@ngrx/store";
import { BehaviorSubject, catchError, finalize, first, map, Observable, shareReplay, tap } from "rxjs";
import { PaymentProviderApi } from '@modules/payment/api/payment-provider.api';
import { PaymentProvider, PaymentProviderOption } from "@modules/payment/models/payment-provider.model";
import { PAYMENT_PROVIDER_STORE, PaymentProviderStore, } from "@modules/payment/store/payment-provider.store";
import { PaymentProviderFactory, paymentProviderFactory } from "@modules/payment/factories/payment-provider.factory";

@Injectable({
  providedIn: 'root'
})
export class PaymentProviderService extends EntityService<EntityFeatureNameEnum.PaymentProvider, PaymentProviderType, PaymentProvider> {
  private readonly _paymentProvidersInitialized$ = new BehaviorSubject(false);
  private readonly _paymentProvidersDirty$ = new BehaviorSubject(false);
  private paymentProvidersRequest$: Observable<PaymentProvider[]> | null = null;

  get activeEntities (): Observable<PaymentProvider[]> {
    return this.entities$.pipe(map(items => items.filter(item => item.isActive)))
  }

  readonly paymentProvidersInitialized$ = this._paymentProvidersInitialized$.asObservable();
  readonly paymentProvidersDirty$ = this._paymentProvidersDirty$.asObservable();

  findByName$ (name: PaymentProviderNameEnum): Observable<PaymentProvider> {
    return this.store.pipe(
      select(this.paymentProviderStore.selectByName(name)),
      map(item => item ? this.factory.create(item) : null)
    )
  }

  constructor (
    protected readonly store: Store,
    @Inject(PAYMENT_PROVIDER_STORE) protected readonly paymentProviderStore: PaymentProviderStore,
    private readonly paymentProviderApi: PaymentProviderApi,
  ) {
    super(store, paymentProviderStore)
  }

  get factory (): PaymentProviderFactory {
    return paymentProviderFactory;
  }

  get paymentProviderOptions$ (): Observable<PaymentProviderOption[]> {
    return this.activeEntities.pipe(
      map(items => items.map(item => ({
        icon: item.icon,
        value: item.name as PaymentProviderNameEnum,
        label: item.label
      })))
    )
  }

  setPaymentProviders(paymentProviders: PaymentProvider[]): void {
    this.dispatchSetEntities(paymentProviders);
    this._paymentProvidersInitialized$.next(true);
    this._paymentProvidersDirty$.next(false);
  }

  markDirty(): void {
    if (this._paymentProvidersInitialized$.value) {
      this._paymentProvidersDirty$.next(true);
    }
  }

  ensureLoaded$(): Observable<PaymentProvider[]> {
    if (this._paymentProvidersInitialized$.value && !this._paymentProvidersDirty$.value) {
      return this.entities$.pipe(first());
    }

    return this.fetchPaymentProviders$();
  }

  ensureFresh$(): Observable<PaymentProvider[]> {
    return this.fetchPaymentProviders$();
  }

  private fetchPaymentProviders$(): Observable<PaymentProvider[]> {
    if (this.paymentProvidersRequest$) {
      return this.paymentProvidersRequest$;
    }

    const fallback$ = this.entities$.pipe(first());
    const request$ = this.paymentProviderApi.get$().pipe(
      first(),
      tap(paymentProviders => this.setPaymentProviders(paymentProviders)),
      catchError((e) => {
        console.error(e);

        return fallback$;
      }),
      finalize(() => {
        this.paymentProvidersRequest$ = null;
      }),
      shareReplay({bufferSize: 1, refCount: false}),
    );

    this.paymentProvidersRequest$ = request$;

    return request$;
  }
}
